const INVALID_FILENAME_CHARS = /[\\/:*?"<>|_\s]+/g;
const EDGE_SEPARATORS = /^[._]+|[._]+$/g;

function sanitizeForFilename(value: string): string {
  return value.replace(INVALID_FILENAME_CHARS, '_').replace(EDGE_SEPARATORS, '');
}

const partPicker = (parts: Intl.DateTimeFormatPart[]) => (type: Intl.DateTimeFormatPartTypes) =>
  parts.find(p => p.type === type)?.value ?? '';

function formatSwissDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // hourCycle h23 pins midnight to 00h00; fr-CH would otherwise render it 24h00.
  const pick = partPicker(
    new Intl.DateTimeFormat('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date),
  );
  return `${pick('day')}.${pick('month')}.${pick('year')}-${pick('hour')}h${pick('minute')}`;
}

function formatSwissDate(date: Date): string {
  const pick = partPicker(new Intl.DateTimeFormat('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(date));
  return `${pick('day')}.${pick('month')}.${pick('year')}`;
}

export interface ExportFilenamePayload {
  assetName?: string | null;
  timestamp?: string | null;
}

export function buildCampaignExportFilename(
  payload: ExportFilenamePayload | null | undefined,
  fallback: { id: number; timestamp?: string | null },
): string {
  const sanitized = payload?.assetName ? sanitizeForFilename(payload.assetName.trim()) : '';
  const namePart = sanitized || 'campaign';
  const dateTimePart = formatSwissDateTime(payload?.timestamp ?? fallback.timestamp);
  // The id guarantees uniqueness: the API only forbids same-minute duplicates among live
  // campaigns, so a cancelled and a live campaign can share an asset, a day and an hour.
  return dateTimePart ? `${namePart}-${dateTimePart}-${fallback.id}.json` : `${namePart}-${fallback.id}.json`;
}

/** Day precision only: the barcode list is reprinted rarely, and two exports the same
 *  day are the same sheet. */
export function buildBarcodeExportFilename(assetName: string | null | undefined, date: Date): string {
  const sanitized = assetName ? sanitizeForFilename(assetName.trim()) : '';
  return `${sanitized || 'measures'}-codes-barres-${formatSwissDate(date)}.csv`;
}
